import { portalColors } from '../../Publications/publicationModel';
// src/components/PropertyReport/utils/pdfUtils.js
import jsPDF from "jspdf";
import "jspdf-autotable";

// Imágenes por defecto (fallbacks)
import COVER_BACKGROUND from "../../../assets/fondo.png";
import DOCUMENTACION from "../../../assets/DOCUMENTACION.png";
import PROP from "../../../assets/prop.png";
import logo from "../../../assets/logo-cotify-horizontal-negro.png";
import { areReportPricesComplete, drawPriceReport } from "./priceReportLayout.js";
import { drawContactReport, resolveReportContact } from "./contactReportLayout.js";

// Utils propios
import { calculateMapCenter, calculateZoomLevel } from "./mapUtils.js";
import { pushReportToDashboard, getUserData, chunkArray } from "./reportUtils.js";
import { uploadReportFile } from "./uploadUtils.js";

// Branding API
import { fetchBranding } from "../../../api/brandingApi.js";
import {
  WIZARD_CHARACTERISTICS,
  formatCharacteristicDisplay,
} from "../../../utils/characteristicFields.js";

const apiUrl = process.env.REACT_APP_API_URL;
const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

/** =========================
 *   Helpers de colores
 * ======================== */
const hexToRgb = (hex = "#FA7317") => {
  const m = hex.replace("#", "").match(/.{1,2}/g);
  if (!m || m.length < 3) return [250, 115, 23];
  return m.slice(0, 3).map((x) => parseInt(x, 16));
};

/** =========================
 *   Carga de imágenes
 * ======================== */
/** Carga una imagen remota pasando por el proxy con auth, devuelve dataURL (PNG). */
export const loadImage = async (url) => {
  try {
    const token =
      localStorage.getItem("auth_token") || localStorage.getItem("token");
    const response = await fetch(
      `${API_URL}/proxy?url=${encodeURIComponent(url)}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const blob = await response.blob();
    const img = new Image();
    img.src = URL.createObjectURL(blob);
    return new Promise((resolve) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        // ✅ Mayor resolución
        const maxWidth = 2400;
        const maxHeight = 2400;
        let { width, height } = img;
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // ✅ PNG para nitidez
        const dataUrl = canvas.toDataURL("image/png");
        resolve(dataUrl);
      };
      img.onerror = () => {
        console.error(`Failed to load image from ${url}`);
        resolve(null);
      };
    });
  } catch (error) {
    console.error(`Error fetching image from proxy: ${error}`);
    return null;
  }
};

/** Convierte un asset local/same-origin en dataURL sin pasar por el proxy. */
async function loadLocalToDataUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Devuelve dataURL siempre (admite data:, http(s) y assets locales importados). */
async function ensureImageDataUrl(src) {
  if (!src) return null;
  if (src.startsWith("data:")) return src;
  const isAbsolute = /^https?:\/\//i.test(src);
  try {
    return isAbsolute ? await loadImage(src) : await loadLocalToDataUrl(src);
  } catch {
    return null;
  }
}

/** Detecta formato desde dataURL. */
const formatFromDataUrl = (dataUrl = "") =>
  dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";

/** Lee dimensiones naturales de un dataURL. */
async function getImageDimensions(dataUrl) {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve({ w: im.width, h: im.height });
    im.onerror = () => resolve(null);
    im.src = dataUrl;
  });
}

/** Ajusta dentro de una caja maxW x maxH manteniendo aspecto. */
function fitWithinBox(natW, natH, maxW, maxH) {
  const ratio = natW / natH;
  let drawW = maxW;
  let drawH = drawW / ratio;
  if (drawH > maxH) {
    drawH = maxH;
    drawW = drawH * ratio;
  }
  return { drawW, drawH };
}

/** Dibuja una imagen (dataURL) ajustada dentro de una caja (x,y,maxW,maxH). */
function addImageFitBoxSync(doc, dataUrl, dims, x, y, maxW, maxH, align = "tl") {
  if (!dataUrl || !dims) return;
  const { drawW, drawH } = fitWithinBox(dims.w, dims.h, maxW, maxH);
  let dx = x, dy = y;
  if (align === "tr") dx = x + (maxW - drawW);
  if (align === "bl") dy = y + (maxH - drawH);
  if (align === "br") { dx = x + (maxW - drawW); dy = y + (maxH - drawH); }
  if (align === "center") { dx = x + (maxW - drawW) / 2; dy = y + (maxH - drawH) / 2; }
  const fmt = formatFromDataUrl(dataUrl);
  doc.addImage(dataUrl, fmt, dx, dy, drawW, drawH);
}

/** Dibuja el logo abajo a la derecha ajustado dentro de una cajita. (Logo BR) */
function drawBRLogoSync(doc, pageWidth, pageHeight, dataUrl, dims, {
  margin = 10, boxW = 22, boxH = 20
} = {}) {
  if (!dataUrl || !dims) return;
  const boxX = pageWidth - boxW - margin;
  const boxY = pageHeight - boxH - margin;
  addImageFitBoxSync(doc, dataUrl, dims, boxX, boxY, boxW, boxH, "br");
}

/** =========================
 *   Wizard normalizer
 * ======================== */
export function normalizeWizard(wd = {}) {
  const sel = wd.selectedPlace || wd.selected_place || {};
  const am = wd.amenities || {};
  const num = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n : undefined;
  };
  const bool = (x) => !!x;

  return {
    type_operation: wd.type_operation ?? wd.typeOperation ?? wd.operationType ?? "",
    type_property: wd.type_property ?? wd.typeProperty ?? "",

    rooms: num(wd.rooms),
    bedrooms: num(wd.bedrooms),
    bathrooms: num(wd.bathrooms),
    square_meters_total: num(wd.square_meters_total ?? wd.totalArea ?? wd.m2_total),
    square_meters_covered: num(wd.square_meters_covered ?? wd.coveredArea ?? wd.m2_covered),
    parking: num(wd.parking),

    ...WIZARD_CHARACTERISTICS.reduce((acc, config) => {
      acc[`${config.formKey}UseRange`] = !!wd[`${config.formKey}UseRange`];
      acc[`${config.formKey}Min`] = wd[`${config.formKey}Min`] ?? "";
      acc[`${config.formKey}Max`] = wd[`${config.formKey}Max`] ?? "";
      acc[config.formKey] = wd[config.formKey] ?? "";
      return acc;
    }, {}),

    has_pool: bool(wd.has_pool ?? wd.hasPool ?? am.Pileta),
    has_grill: bool(wd.has_grill ?? wd.hasGrill ?? am.Parrilla),
    has_garden: bool(wd.has_garden ?? wd.hasGarden ?? am["Jardín"]),
    has_terrace: bool(wd.has_terrace ?? wd.hasTerrace ?? am.Terraza),
    has_balcony: bool(wd.has_balcony ?? wd.hasBalcony ?? am.Balcón),

    selectedPlace: {
      lat: num(sel.lat),
      lng: num(sel.lng),
      formatted_address: sel.formatted_address ?? wd.address ?? "",
    },

    session_id: wd.session_id,
  };
}

// naming decente para tipos si el map no los tiene
const humanizeType = (slug = "") =>
  slug ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "N/A";

/** =========================
 *   Páginas, header/footer
 * ======================== */
const addFullBleedImage = (doc, dataUrl, pageWidth, pageHeight) => {
  const fmt = formatFromDataUrl(dataUrl);
  doc.addImage(dataUrl, fmt, 0, 0, pageWidth, pageHeight);
};

// header/footer con color de marca (NO usar en portada ni en contacto)
export const addHeaderFooter = (doc, _pageNumber, brandRGB = [250, 115, 23]) => {
  const [r, g, b] = brandRGB;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pageWidth, 3, "F");
  doc.setFillColor(r, g, b);
  doc.rect(0, pageHeight - 3, pageWidth, 10, "F");
};

/** Carga la foto/logo y usa siempre Cotify si falta o no se puede leer. */
async function loadContactImage(source, fit) {
  for (const candidate of [source, logo].filter(Boolean)) {
    const data = await ensureImageDataUrl(candidate);
    if (!data) continue;
    const dims = await getImageDimensions(data);
    if (!dims?.w || !dims?.h) continue;
    const fallback = candidate === logo;
    return { data, dims, format: formatFromDataUrl(data), fallback, fit: fallback ? "contain" : fit };
  }
  return null;
}

/** =========================
 *   Render de texto formateado
 * ======================== */
export const renderFormattedText = (
  doc,
  text,
  x,
  y,
  maxWidth,
  lineHeight = 7
) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 20;
  let currentY = y;

  const rawLines = doc.splitTextToSize(text, maxWidth);

  rawLines.forEach((rawLine) => {
    const headingMatch = rawLine.match(/^(#{1,4})\s+(.*)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const lineContent = headingMatch[2];
      let fontSize = level === 1 ? 16 : level === 2 ? 14 : 12;
      try { doc.setFont("helvetica", "bold"); } catch { }
      doc.setFontSize(fontSize);
      if (currentY + lineHeight > pageHeight - bottomMargin) {
        doc.addPage();
        currentY = 20;
      }
      doc.text(lineContent, x, currentY);
      currentY += lineHeight;
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setFontSize(12);
    } else {
      const segments = rawLine.split("**");
      segments.forEach((segment, index) => {
        const fontStyle = index % 2 === 1 ? "bold" : "normal";
        try { doc.setFont("helvetica", fontStyle); } catch { }
        const lines = doc.splitTextToSize(segment, maxWidth);
        lines.forEach((line) => {
          if (currentY + lineHeight > pageHeight - bottomMargin) {
            doc.addPage();
            currentY = 20;
          }
          doc.text(line, x, currentY);
          currentY += lineHeight;
        });
      });
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setFontSize(12);
    }
  });
};

/** =========================
 *   Render rich text (markdown → PDF) — usado por las páginas custom
 *   creadas en el editor. Soporta:
 *     - # ## ### headings
 *     - - / * bullets
 *     - **negrita** y *itálica* INLINE (no en líneas separadas como
 *       renderFormattedText hace por error)
 *     - tablas markdown | col | col |  (usa jspdf-autotable)
 *
 *   Color y font customizables — por defecto #545454 + Helvetica.
 * ======================== */
/**
 * Helper: renderiza una línea con negritas inline (**texto** o __texto__).
 * Wrap palabra-por-palabra. Usado por párrafos Y bullets para que el
 * formato sea consistente. `indent` indenta las líneas wrap (no la primera).
 */
const renderInlineSegmentedLine = (doc, line, x, maxWidth, opts) => {
  const { lineHeight, indent = 0, ensureSpace, setCurrentY, getCurrentY } = opts;
  // Parser de negritas con regex global — tolera **bold** y __bold__.
  const boldRe = /(\*\*([^*\n]+?)\*\*|__([^_\n]+?)__)/g;
  const segments = [];
  let lastIdx = 0;
  let m;
  while ((m = boldRe.exec(line)) !== null) {
    if (m.index > lastIdx) {
      segments.push({ text: line.slice(lastIdx, m.index), bold: false });
    }
    const inner = (m[2] || m[3] || "").trim();
    if (inner) segments.push({ text: inner, bold: true });
    lastIdx = boldRe.lastIndex;
  }
  if (lastIdx < line.length) {
    segments.push({ text: line.slice(lastIdx), bold: false });
  }
  if (segments.length === 0) segments.push({ text: line, bold: false });

  let cursorX = x;
  let lineSegments = [];
  let isFirstLine = true;

  const flushLine = () => {
    if (lineSegments.length === 0) return;
    ensureSpace();
    let drawX = isFirstLine ? x : x + indent;
    lineSegments.forEach((seg) => {
      try { doc.setFont("helvetica", seg.bold ? "bold" : "normal"); } catch { }
      doc.text(seg.text, drawX, getCurrentY());
      drawX += doc.getTextWidth(seg.text);
    });
    setCurrentY(getCurrentY() + lineHeight);
    cursorX = x;
    lineSegments = [];
    isFirstLine = false;
  };

  segments.forEach((seg) => {
    try { doc.setFont("helvetica", seg.bold ? "bold" : "normal"); } catch { }
    const words = seg.text.split(/(\s+)/);
    words.forEach((w) => {
      if (!w) return;
      const wW = doc.getTextWidth(w);
      const lineWidth = maxWidth - (isFirstLine ? 0 : indent);
      if (cursorX - x + wW > lineWidth && lineSegments.length > 0) {
        flushLine();
      }
      lineSegments.push({ text: w, bold: seg.bold });
      cursorX += wW;
    });
  });
  flushLine();
  try { doc.setFont("helvetica", "normal"); } catch { }
};

export const renderRichText = (
  doc,
  text,
  x,
  y,
  maxWidth,
  {
    color = [84, 84, 84],
    baseFontSize = 12,
    // Calibrado a 7.5mm con font 12pt = ratio 1.77. Antes 6mm (1.42) quedaba
    // apretado; 7mm (1.66) seguía corto en pantalla. 7.5mm da el aire que
    // matchea visualmente al preview React.
    lineHeight = 7.5,
  } = {}
) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomMargin = 25;
  let currentY = y;
  let prevBlockType = null; // 'heading' | 'paragraph' | 'bullets' | 'table' | null

  const ensureSpace = (needed = lineHeight) => {
    if (currentY + needed > pageHeight - bottomMargin) {
      doc.addPage();
      currentY = 25;
      prevBlockType = null;
    }
  };

  // Agrega un margen vertical ANTES del próximo bloque según el tipo del
  // bloque anterior y el siguiente. Replica los `margin: 6px 0 3px` del
  // MarkdownPreview React → da respiración entre headings/párrafos/tablas.
  const blockGap = (next) => {
    if (prevBlockType === null) return 0;
    // gaps en mm — calibrados para que respiren visualmente. Antes los
    // bloques quedaban pegados (sobre todo bullets→paragraph y entre
    // párrafos). Subimos los gaps para que cada bloque tenga aire.
    if (next === "heading") return 5;
    if (next === "table") return 4;
    if (next === "bullets") return 3;
    if (prevBlockType === "heading") return 3;
    if (prevBlockType === "table") return 4;
    if (prevBlockType === "bullets") return 3;
    return 3; // paragraph → paragraph
  };

  doc.setTextColor(...color);

  // Tokenizamos el texto en BLOQUES (heading, bullets, tabla, párrafo).
  const lines = (text || "").split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Heading
    const h = line.match(/^(#{1,3})\s+(.*)/);
    if (h) {
      const level = h[1].length;
      const size = level === 1 ? baseFontSize * 1.5
        : level === 2 ? baseFontSize * 1.3
        : baseFontSize * 1.1;
      currentY += blockGap("heading");
      try { doc.setFont("helvetica", "bold"); } catch { }
      doc.setFontSize(size);
      ensureSpace(size * 0.5);
      doc.text(h[2], x, currentY);
      currentY += size * 0.5 + 2;
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setFontSize(baseFontSize);
      prevBlockType = "heading";
      i++;
      continue;
    }

    // Tabla markdown — necesita una línea de header + separador "|---|"
    if (line.match(/^\s*\|.*\|\s*$/) && i + 1 < lines.length && lines[i + 1].match(/^\s*\|[-:\s|]+\|\s*$/)) {
      const headerCells = line.split("|").slice(1, -1).map((c) => c.trim());
      const bodyRows = [];
      i += 2;
      while (i < lines.length && lines[i].match(/^\s*\|.*\|\s*$/)) {
        bodyRows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      currentY += blockGap("table");
      ensureSpace(20);
      try {
        doc.autoTable({
          startY: currentY,
          margin: { left: x, right: doc.internal.pageSize.getWidth() - x - maxWidth },
          head: [headerCells],
          body: bodyRows,
          theme: "grid",
          styles: {
            font: "helvetica",
            fontSize: baseFontSize - 1,
            textColor: color,
            cellPadding: 2.5,
            lineColor: [226, 232, 240],
            lineWidth: 0.2,
          },
          headStyles: {
            fillColor: [241, 245, 249],
            textColor: [15, 23, 42],
            fontStyle: "bold",
          },
          alternateRowStyles: {
            fillColor: [250, 252, 254],
          },
        });
        currentY = (doc.lastAutoTable?.finalY || currentY) + 4;
      } catch (err) {
        console.warn("autoTable falló, render plano:", err);
        [headerCells.join(" | "), ...bodyRows.map(r => r.join(" | "))]
          .forEach((row) => {
            ensureSpace();
            doc.text(row, x, currentY);
            currentY += lineHeight;
          });
      }
      prevBlockType = "table";
      continue;
    }

    // Bullets — agrupamos consecutivos. Cada item se renderiza con el MISMO
    // parser de negritas inline que los párrafos (antes usaba doc.text plain
    // y los **bold** salían como literal en el PDF — por eso "a veces las
    // negritas se ven y a veces no", quedaba inconsistente entre párrafos
    // y bullets).
    if (line.match(/^\s*[-*]\s+/)) {
      currentY += blockGap("bullets");
      while (i < lines.length && lines[i].match(/^\s*[-*]\s+/)) {
        const item = lines[i].replace(/^\s*[-*]\s+/, "");
        renderInlineSegmentedLine(doc, "•  " + item, x, maxWidth, {
          lineHeight,
          indent: 4, // wrap lines indentadas para alinear con el primer char
          ensureSpace,
          setCurrentY: (newY) => { currentY = newY; },
          getCurrentY: () => currentY,
        });
        i++;
      }
      prevBlockType = "bullets";
      continue;
    }

    // Línea vacía
    if (line.trim() === "") {
      currentY += lineHeight * 0.5;
      i++;
      continue;
    }

    // Párrafo normal — usa el helper compartido con bullets.
    currentY += blockGap("paragraph");
    renderInlineSegmentedLine(doc, line, x, maxWidth, {
      lineHeight,
      indent: 0,
      ensureSpace,
      setCurrentY: (newY) => { currentY = newY; },
      getCurrentY: () => currentY,
    });
    prevBlockType = "paragraph";
    i++;
  }
};

/** =========================
 *   Generación del PDF
 * ======================== */
export const generatePDF = async ({
  properties,
  reportName,
  includeCustomReport,
  customContent,
  setLoadingReport,
  wizardData,
  reportPrices = [],
  reportContact = null,
  currencyMode = "USD",
  commissionPercent,
  // IDs de páginas adicionales que el user ocultó desde el editor visual.
  // Se filtran de las URLs de branding ANTES de armar el PDF.
  hiddenPageIds = [],
  // Páginas vacías creadas por el user en el editor. Cada una se imprime
  // como hoja blanca con header brand + título + body, ANTES de Contacto.
  // Forma: [{ id, title, body }].
  customPages = [],
  options = { download: true, upload: true },
}) => {
  setLoadingReport(true);
  const { download = true, upload = true } = options;
  const W = normalizeWizard(wizardData);
  const isHidden = (id) => hiddenPageIds.includes(id);

  try {
    if (!areReportPricesComplete(reportPrices)) {
      throw new Error("Completá las cuatro alternativas con precios mayores a cero.");
    }
    // 0) Branding
    const brand = await fetchBranding().catch(() => null);
    const brandRGB = hexToRgb(brand?.primary_color || "#FA7317");

    // Logo BR para todas menos portada/contacto
    const companyLogoDataUrl = await ensureImageDataUrl(brand?.logo_url || logo);
    const companyLogoDims = companyLogoDataUrl
      ? await getImageDimensions(companyLogoDataUrl)
      : null;

    // 1) Setup doc
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [297, 320],
    });
    let currentPage = 1;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const typePropertyMap = {
      "quintas-vacacionales": "Quinta Vacacional",
      "campos": "Campo",
      "bóvedas-nichos-y-parcelas": "Bóveda, Nicho y Parcela",
      "terrenos": "Terreno",
      "cocheras": "Cochera",
      "desarrollos-horizontales": "Desarrollo Horizontale",
      "desarrollos-verticales": "Desarrollo Vertical",
      "hoteles": "Hotele",
      "oficinas-comerciales": "Oficina Comercial",
      "depósitos": "Depósito",
      "locales-comerciales": "Local Comercial",
      "edificios": "Edificio",
      "bodegas-galpones": "Bodega/Galpone",
      "fondos-de-comercio": "Fondo de Comercio",
      "casas": "Casa",
      "departamentos": "Departamento",
      "oficinas": "Oficina",
      "consultorios": "Consultorio",
      "ph": "PH",
    };

    // 2) Portada (SIN header/footer/BR)
    if (brand?.cover_image_url) {
      const cov = await loadImage(brand.cover_image_url);
      if (cov) addFullBleedImage(doc, cov, pageWidth, pageHeight);
      else doc.addImage(COVER_BACKGROUND, "PNG", 0, 0, pageWidth, pageHeight);
    } else {
      doc.addImage(COVER_BACKGROUND, "PNG", 0, 0, pageWidth, pageHeight);
    }

    try { doc.setFont("helvetica", "bold"); } catch { }
    doc.setFontSize(28);
    doc.setTextColor(0, 0, 0);

    // 3) Detalles propiedad (CON header/footer/BR)
    doc.addPage();
    currentPage++;
    doc.addImage(PROP, "PNG", 0, 0, pageWidth, pageHeight);
    addHeaderFooter(doc, currentPage, brandRGB);

    if (W) {
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setTextColor(115, 115, 115);

      // Dirección
      doc.setFontSize(23);
      const address =
        W.selectedPlace?.formatted_address || "Dirección no especificada";
      const maxAddressWidth = 200;
      const addressLines = doc.splitTextToSize(address, maxAddressWidth);
      doc.setDrawColor(...brandRGB);
      doc.setLineWidth(1.4);
      doc.line(40, 80, 50, 80);
      const addressY0 = 60;
      addressLines.forEach((line, idx) => {
        doc.text(line, 45, addressY0 + idx * 10);
      });

      // layout
      const column1X = 52;
      const column2X = 210;
      let yPosition = 103;
      const lineHeight = 18.9;

      const formatAmenities = (data) => {
        const amenities = [];
        if (data.parking) amenities.push("Cochera");
        if (data.has_pool) amenities.push("Pileta");
        if (data.has_grill) amenities.push("Parrilla");
        if (data.has_garden) amenities.push("Jardín");
        if (data.has_terrace) amenities.push("Terraza");
        if (data.has_balcony) amenities.push("Balcón");
        return amenities.join(", ") || "Ninguna especificada";
      };

      const propertyData = [
        { label: "Tipo de propiedad", value: typePropertyMap[W.type_property] || humanizeType(W.type_property) },
        { label: "Superficie total", value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[4], " m²") },
        { label: "Superficie cubierta", value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[3], " m²") },
        { label: "Cochera", value: (W.parking ?? "N/A").toString() },
        { label: "Baños", value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[2]) },
        { label: "Ambientes", value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[0]) },
        { label: "Dormitorios", value: formatCharacteristicDisplay(W, WIZARD_CHARACTERISTICS[1]) },
      ];

      doc.setFontSize(14);
      for (let i = 0; i < 7; i++) {
        const isSecondColumn = i >= 4;
        const columnX = isSecondColumn ? column2X : column1X;
        const itemIndex = isSecondColumn ? i - 4 : i;
        const y = yPosition + itemIndex * lineHeight;

        try { doc.setFont("helvetica", "bold"); } catch { }
        const labelText =
          propertyData[i].label === "Baños" ? "Baños" : propertyData[i].label;
        doc.text(`${labelText}:`, columnX, y);

        try { doc.setFont("helvetica", "normal"); } catch { }
        doc.setFontSize(13);
        doc.text(propertyData[i].value, columnX + 50, y);
        doc.setFontSize(14);
      }

      // Logo BR
      drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims, {
        margin: 10,
        boxW: 22,
        boxH: 20,
      });

      // Comodidades
      const amenitiesText = formatAmenities(W);
      const maxAmenitiesWidth = 200;
      const amenitiesLines = doc.splitTextToSize(amenitiesText, maxAmenitiesWidth);
      let amenitiesY = yPosition + 3 * lineHeight + 52;

      try { doc.setFont("helvetica", "bold"); } catch { }
      doc.setFontSize(14);
      doc.text("Comodidades:", column1X, amenitiesY);

      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setFontSize(13);
      amenitiesLines.forEach((line, idx) => {
        doc.text(line, column1X + 35, amenitiesY + idx * 10);
      });
    } else {
      doc.setFontSize(16);
      doc.text("No se proporcionaron datos del formulario", 20, 60);
    }

    // 4) Mapa (tamaño actual)
    doc.addPage();
    currentPage++;
    addHeaderFooter(doc, currentPage, brandRGB);
    try { doc.setFont("helvetica", "bold"); } catch { }
    doc.setFontSize(16);
    doc.setTextColor(50, 50, 50);
    doc.text("Ubicación de propiedades comparadas", 20, 30);
    doc.setDrawColor(...brandRGB);
    doc.setLineWidth(1);
    doc.line(20, 34, 41, 34);

    const validProperties = properties.filter((p) => p.lat && p.lng);
    if (validProperties.length === 0) {
      doc.setFontSize(12);
      try { doc.setFont("helvetica", "bold"); } catch { }
      doc.text("No hay propiedades con coordenadas válidas para mostrar el mapa.", 10, 30);
    } else {
      const subjectLat = Number(W?.selectedPlace?.lat);
      const subjectLng = Number(W?.selectedPlace?.lng);
      const subjectMarker =
        Number.isFinite(subjectLat) && Number.isFinite(subjectLng)
          ? `markers=color:blue%7Clabel:S%7C${subjectLat},${subjectLng}`
          : null;

      const compMarkers = validProperties
        .map((p, i) => `markers=color:${portalColors(p)[0].replace('#', '0x')}%7Clabel:${i + 1}%7C${p.lat},${p.lng}`)
        .join("&");

      const pointsForCenter = [...validProperties];
      if (subjectMarker) pointsForCenter.push({ lat: subjectLat, lng: subjectLng });

      const center = calculateMapCenter(pointsForCenter);
      const zoom = calculateZoomLevel(pointsForCenter);
      const markersParam = subjectMarker
        ? `${subjectMarker}&${compMarkers}`
        : compMarkers;

      const mapUrl =
        `https://maps.googleapis.com/maps/api/staticmap` +
        `?center=${center.lat},${center.lng}` +
        `&zoom=${zoom}` +
        `&size=600x600&scale=2&maptype=roadmap` +
        `&${markersParam}` +
        `&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;

      const mapImage = await loadImage(mapUrl);

      // Logo BR
      drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims, {
        margin: 10,
        boxW: 22,
        boxH: 20,
      });

      if (mapImage) {
        const imgWidth = 180;
        const imgHeight = 180;
        const centerX = (pageWidth - imgWidth) / 2;
        const centerY = (pageHeight - imgHeight) / 2;
        doc.addImage(mapImage, "PNG", centerX, centerY, imgWidth, imgHeight);
      } else {
        doc.setFontSize(12);
        doc.text("No se pudo cargar el mapa", 10, 30);
      }
    }

    // 5) Comparativa (múltiples páginas)
    doc.addPage();
    currentPage++;
    addHeaderFooter(doc, currentPage, brandRGB);
    try { doc.setFont("helvetica", "bold"); } catch { }
    doc.setFontSize(16);
    doc.setTextColor(50, 50, 50);
    doc.text("Comparativa de Propiedades", 20, 30);
    doc.setDrawColor(...brandRGB);
    doc.setLineWidth(1);
    doc.line(20, 34, 41, 34);

    const imagePromises = properties.map((p) => loadImage(p.image_url));
    const images = await Promise.all(imagePromises);
    const chunkedProps = chunkArray(properties, 6);
    const chunkedImages = chunkArray(images, 6);

    const truncateAddress2Lines = (doc, text, maxWidth) => {
      const t = (text || "").toString();
      const prevSize = doc.getFontSize();
      let prevFamily = "helvetica",
        prevStyle = "normal";
      try {
        const f = doc.getFont?.();
        prevFamily = f?.fontName || "helvetica";
        prevStyle = f?.fontStyle || "normal";
      } catch { }
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setFontSize(8);
      const lines = doc.splitTextToSize(t, maxWidth);
      try { doc.setFont(prevFamily, prevStyle); } catch { }
      doc.setFontSize(prevSize);
      if (lines.length <= 2) return lines.join("\n");
      let second = lines[1];
      if (second.length > 3) second = second.slice(0, -3) + "…";
      else second = second + "…";
      return `${lines[0]}\n${second}`;
    };
    const CELL_W = 42;
    const PAD = 2;
    const MAX_W = CELL_W - PAD * 2;

    chunkedProps.forEach((propGroup, index) => {
      const imageGroup = chunkedImages[index];
      if (index > 0) {
        doc.addPage();
        currentPage++;
        addHeaderFooter(doc, currentPage, brandRGB);
        try { doc.setFont("helvetica", "bold"); } catch { }
        doc.setFontSize(16);
        doc.text("Comparativa de Propiedades", 20, 30);
        doc.setDrawColor(...brandRGB);
        doc.setLineWidth(1);
        doc.line(20, 34, 41, 34);
      }

      // Logo BR
      drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims, {
        margin: 10,
        boxW: 22,
        boxH: 20,
      });

      const headRow = [
        "Dato",
        ...propGroup.map((_, i) => `Prop. ${6 * index + (i + 1)}`),
      ];

      const bodyData = [
        [
          "Imagen",
          ...imageGroup.map((img) => (img ? { content: "", imageData: img } : "N/A")),
        ],
        [
          "Link a la propiedad",
          ...propGroup.map((p) =>
            p.url
              ? {
                  content: "Link",
                  link: p.url,
                  styles: { textColor: [0, 0, 255], fontStyle: "underline" },
                }
              : "N/A"
          ),
        ],
        [
          "Google Maps",
          ...propGroup.map((p) =>
            p.lat && p.lng
              ? {
                  content: "Ver Mapa",
                  link: `https://www.google.com/maps?q=${p.lat},${p.lng}`,
                  styles: { textColor: [0, 0, 255], fontStyle: "underline" },
                }
              : "N/A"
          ),
        ],
        ["Precio", ...propGroup.map((p) => `${p.price_currency} ${p.price?.toLocaleString() || "0"}`)],
        ["Expensas", ...propGroup.map((p) => `${p.expenses_currency} ${p.expenses_value?.toLocaleString() || ""}`)],
        [
          "Valor por m²",
          ...propGroup.map((p) => {
            const pricePerSquareMeter =
              p.price && p.square_meters_total
                ? (p.price / p.square_meters_total).toFixed(2)
                : "N/A";
            return `${p.price_currency} ${pricePerSquareMeter}`;
          }),
        ],
        ["Dirección", ...propGroup.map((p) => truncateAddress2Lines(doc, p.address || "", MAX_W))],
        ["Superficie Total", ...propGroup.map((p) => (p.square_meters_total || "") + " m²")],
        ["Superficie Cubierta", ...propGroup.map((p) => (p.square_meters_covered || "") + " m²")],
        ["Ambientes", ...propGroup.map((p) => p.rooms || "")],
        ["Dormitorios", ...propGroup.map((p) => p.bedrooms || "")],
        ["Baños", ...propGroup.map((p) => p.bathrooms || "")],
        ["Jardín", ...propGroup.map((p) => (p.hasGarden ? "Sí" : "No"))],
        ["Pileta", ...propGroup.map((p) => (p.hasPool ? "Sí" : "No"))],
        ["Parrilla", ...propGroup.map((p) => (p.hasGrill ? "Sí" : "No"))],
        ["Terraza", ...propGroup.map((p) => (p.hasTerrace ? "Sí" : "No"))],
        ["Cocheras", ...propGroup.map((p) => p.parking || "")],
        ["Antigüedad", ...propGroup.map((p) => (p.antiquity || "") + " años")],
        ["Operación", ...propGroup.map((p) => p.typeOperation || "")],
        ["Propiedad", ...propGroup.map((p) => p.typeProperty || "")],
        ["Días Publicado", ...propGroup.map((p) => p.days_published || "+")],
        ["Visualizaciones", ...propGroup.map((p) => p.views || "")],
        ["Inmobiliaria", ...propGroup.map((p) => (p.owner_type || "") + (p.publication_count > 1 ? ` · ${p.publication_count} anuncios / ${p.agency_count} inmobiliarias` : ""))],
      ];

      doc.autoTable({
        startY: 40,
        head: [headRow],
        body: bodyData,

        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
          textColor: [55, 55, 55],
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [242, 242, 242],
          textColor: [40, 40, 40],
          fontStyle: "bold",
          halign: "center",
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        columnStyles: {
          0: { cellWidth: 42 },
          1: { cellWidth: 42 },
          2: { cellWidth: 42 },
          3: { cellWidth: 42 },
          4: { cellWidth: 42 },
          5: { cellWidth: 42 },
          6: { cellWidth: 42 },
        },
        bodyStyles: { cellWidth: "auto" },
        margin: { top: 20, left: 10, right: 10 },
        pageBreak: "auto",

        didDrawPage: (data) => {
          if (data.pageNumber > currentPage) {
            currentPage = data.pageNumber;
            addHeaderFooter(doc, currentPage, brandRGB);
          }
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.row.index === 0) {
            data.row.height = 50; // altura para la fila de imágenes
          }
        },
        didDrawCell: (data) => {
          const { doc, cell, row } = data;
          const cellData = cell.raw;
          if (data.section === "body" && row.index === 0 && data.column.index > 0) {
            if (cellData && cellData.imageData) {
              doc.addImage(cellData.imageData, "PNG", cell.x + 2, cell.y + 2, 40, 40);
            }
          }
          if (cellData && typeof cellData === "object" && cellData.link) {
            doc.link(cell.x, cell.y, cell.width, cell.height, { url: cellData.link });
          }
        },
      });
    });

    /** =========================
     *   6) PRECIO: cuatro alternativas editables, impresas como vectores.
     *   El fondo personalizado queda detrás de la composición de precios.
     * ======================== */
    doc.addPage();
    currentPage++;
    {
      // Fondo (custom o por defecto)
      const customPrice = brand?.additional_page1_url
        ? await loadImage(brand.additional_page1_url)
        : null;

      if (customPrice) {
        addFullBleedImage(doc, customPrice, pageWidth, pageHeight);
      }

      // Capa de header/footer/logo
      addHeaderFooter(doc, currentPage, brandRGB);
      drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims);

      drawPriceReport(doc, reportPrices, currencyMode);
    }

    /** =========================
     *   7) (OPCIONAL) additional_page3_url entre Precio y Documentación
     *   (Con header, footer y logo BR)
     * ======================== */
    if (brand?.additional_page3_url && !isHidden("extra3")) {
      const p3 = await loadImage(brand.additional_page3_url);
      if (p3) {
        doc.addPage();
        currentPage++;
        addFullBleedImage(doc, p3, pageWidth, pageHeight);
        addHeaderFooter(doc, currentPage, brandRGB);
        drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims);
      }
    }

    /** =========================
     *   8) DOCUMENTACIÓN (reemplazable por additional_page2_url)
     *   (Con header, footer y logo BR; SIEMPRE muestra % comisión)
     * ======================== */
    doc.addPage();
    currentPage++;
    {
      // Fondo (custom o por defecto)
      const customDocs = brand?.additional_page2_url
        ? await loadImage(brand.additional_page2_url)
        : null;

      if (customDocs) {
        addFullBleedImage(doc, customDocs, pageWidth, pageHeight);
      } else {
        doc.addImage(DOCUMENTACION, "PNG", 0, 0, pageWidth, pageHeight);
      }

      // Capa de header/footer/logo
      addHeaderFooter(doc, currentPage, brandRGB);
      drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims);

      // ⬇️ % Comisión SIEMPRE visible sobre el fondo
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setTextColor(84, 84, 84);
      doc.setFontSize(17);
      const comX = pageWidth * 0.345;
      const comY = 180.5;
      const pct = (commissionPercent ?? "").toString().trim();
      if (pct) {
        doc.text(`${pct}`, comX, comY, { align: "center" });
      }
    }

    /** =========================
     *   9) (OPCIONAL) additional_page4_url antes de Contacto
     *   (Con header, footer y logo BR)
     * ======================== */
    if (brand?.additional_page4_url && !isHidden("extra4")) {
      const p4 = await loadImage(brand.additional_page4_url);
      if (p4) {
        doc.addPage();
        currentPage++;
        addFullBleedImage(doc, p4, pageWidth, pageHeight);
        addHeaderFooter(doc, currentPage, brandRGB);
        drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims);
      }
    }

    /** =========================
     *   9b) CUSTOM PAGES (creadas por el user en el editor)
     *   Cada una se imprime como hoja blanca con:
     *     - barras brand (addHeaderFooter)
     *     - logo BR abajo a la derecha
     *     - título grande (20pt bold)
     *     - body (12pt normal) con renderFormattedText
     * ======================== */
    if (Array.isArray(customPages) && customPages.length > 0) {
      for (const cp of customPages) {
        if (!cp) continue;
        // Si está vacío todo, saltamos para no imprimir hojas en blanco.
        const title = (cp.title || "").trim();
        const body = (cp.body || "").trim();
        if (!title && !body) continue;
        doc.addPage();
        currentPage++;
        // Fondo blanco (sin imagen). Header brand + logo.
        addHeaderFooter(doc, currentPage, brandRGB);
        drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims);
        // Margen lateral 20mm, título arriba después del header brand.
        const padX = 20;
        const titleY = 30;
        const bodyY = 50;
        if (title) {
          try { doc.setFont("helvetica", "bold"); } catch { }
          doc.setFontSize(20);
          // Color #545454 igual que el preview / pedido del cliente.
          doc.setTextColor(84, 84, 84);
          const titleLines = doc.splitTextToSize(title, pageWidth - padX * 2);
          titleLines.forEach((line, idx) => {
            doc.text(line, padX, titleY + idx * 9);
          });
        }
        if (body) {
          // renderRichText: soporta headings, bullets, **negrita** inline y
          // tablas markdown. Color #545454. Dejamos que renderRichText use su
          // default de lineHeight: antes le pasábamos 6 explícito y pisaba
          // el nuevo default de 7 → las líneas dentro de un párrafo salían
          // pegadas una sobre otra.
          renderRichText(doc, body, padX, bodyY, pageWidth - padX * 2, {
            color: [84, 84, 84],
            baseFontSize: 12,
          });
        }
      }
    }

    /** =========================
     *   10) CONTACTO / CIERRE: composición vectorial y datos del informe.
     * ======================== */
    doc.addPage();
    currentPage++;
    {
      const contact = resolveReportContact(brand, reportContact || {});
      const [contactLogo, contactAvatar] = await Promise.all([
        loadContactImage(brand?.logo_url, "contain"),
        loadContactImage(contact.photoUrl, "cover"),
      ]);
      drawContactReport(doc, contact, brand?.primary_color, { logo: contactLogo, avatar: contactAvatar });
    }

    // 11) Informe personalizado (opcional)
    if (includeCustomReport && customContent) {
      doc.addPage();
      currentPage++;
      addHeaderFooter(doc, currentPage, brandRGB);
      try { doc.setFont("helvetica", "bold"); } catch { }
      doc.setFontSize(16);
      doc.text("Informe Personalizado", 20, 30);
      try { doc.setFont("helvetica", "normal"); } catch { }
      doc.setFontSize(12);
      renderFormattedText(
        doc,
        customContent,
        20,
        40,
        doc.internal.pageSize.width - 40
      );
      drawBRLogoSync(doc, pageWidth, pageHeight, companyLogoDataUrl, companyLogoDims);
    }

    // 12) Salida
    const pdfBlob = doc.output("blob");

    if (download) {
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      // Asegurar extensión .pdf — antes el reportName no incluía la extensión
      // y el browser descargaba un archivo sin tipo (ej. "Tasación-2026-06-10"
      // que el SO no reconocía como PDF).
      const baseName = reportName || "informe_propiedades";
      const fileName = /\.pdf$/i.test(baseName) ? baseName : `${baseName}.pdf`;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(downloadUrl);
    }

    let uploadResult = null;
    if (upload) {
      const { url, expires_at } = await uploadReportFile(
        pdfBlob,
        reportName,
        W.session_id,
        "pdf"
      );
      uploadResult = { url, expires_at };

      const userData = getUserData?.();
      if (userData) {
        await pushReportToDashboard({
          reportType: "pdf",
          generatedAt: new Date().toISOString(),
          fileName: reportName || "property_report",
          userId: userData.userId,
          companyId: userData.companyId,
          parameters: {
            properties_ids: properties.map((p) => p.id),
            includeCustomReport,
          },
          storage: { url, expires_at },
        });
      }
    }
    // Devolvemos el resultado del upload (o null si no se subió) para que
    // el caller pueda mostrar la URL en los botones de share.
    return uploadResult;
  } catch (error) {
    console.error("Error generando PDF", error);
    throw error;
  } finally {
    setLoadingReport(false);
  }
};
